/**
 * Brain Server API client — all calls go through CF Functions proxy at /api/brain/*.
 */

import type { BrainSession, WSConfig, DailyCosts, WorkOrderFilters, CreateWorkOrderData, BrainMessage, BrainAgent, MountedBrain, AgentCard } from '../types/brain'
import type { WorkOrder } from '../types/work-orders'
import type { BrainApproval } from '../types/approvals'

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const text = await response.text()
    // Always include the status code so callers can match on "Request failed (NNN)"
    // even when the response body is non-empty (e.g. JSON error objects from CF Functions).
    throw new Error(`Request failed (${response.status})${text ? ': ' + text : ''}`)
  }
  return response.json()
}

export function getCsrfToken(): string {
  const match = document.cookie.match(/csrf_token=([^;]+)/)
  return match ? match[1] : ''
}

function mutationHeaders(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    'X-CSRF-Token': getCsrfToken(),
  }
}

// --- WebSocket Config ---

export async function getWSConfig(): Promise<WSConfig> {
  const res = await fetch('/api/brain/ws-config', { credentials: 'include' })
  return handleResponse<WSConfig>(res)
}

// --- Sessions ---

export async function fetchSessions(limit = 20): Promise<BrainSession[]> {
  const res = await fetch(`/api/brain/sessions?limit=${limit}`, { credentials: 'include' })
  return handleResponse<BrainSession[]>(res)
}

// --- Work Orders (via brain server) ---

export async function fetchBrainWorkOrders(filters?: WorkOrderFilters): Promise<WorkOrder[]> {
  const params = new URLSearchParams()
  if (filters?.status) params.set('status', filters.status)
  if (filters?.to) params.set('to', filters.to)
  if (filters?.from) params.set('from', filters.from)
  const qs = params.toString()
  const res = await fetch(`/api/brain/work-orders${qs ? '?' + qs : ''}`, { credentials: 'include' })
  return handleResponse<WorkOrder[]>(res)
}

export async function fetchBrainWorkOrder(id: string): Promise<WorkOrder> {
  const res = await fetch(`/api/brain/work-orders/${encodeURIComponent(id)}`, { credentials: 'include' })
  return handleResponse<WorkOrder>(res)
}

export async function createBrainWorkOrder(data: CreateWorkOrderData): Promise<WorkOrder> {
  const res = await fetch('/api/brain/work-orders', {
    method: 'POST',
    credentials: 'include',
    headers: mutationHeaders(),
    body: JSON.stringify(data),
  })
  return handleResponse<WorkOrder>(res)
}

export interface AudioToWorkOrderResult {
  success: boolean
  created: boolean
  degraded?: boolean
  reason?: string
  transcriptPreview?: string
  processingErrors?: Array<{
    stage: 'transcription' | 'classification'
    message: string
  }>
  classification?: {
    category: 'WORK' | 'PERSONAL' | 'MIXED'
    confidence: number
    summary: string
    suggestedTitle: string
    ideas: string[]
    topics: string[]
  }
  workOrder?: WorkOrder
}

export async function createBrainWorkOrderFromAudio(input: {
  audio: File
  to: string
  from?: string
  priority?: 'low' | 'medium' | 'high' | 'critical'
}): Promise<AudioToWorkOrderResult> {
  const form = new FormData()
  form.append('audio', input.audio, input.audio.name)
  form.append('to', input.to)
  form.append('from', input.from || 'founder')
  form.append('priority', input.priority || 'medium')

  const res = await fetch('/api/brain/work-orders/from-audio', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'X-CSRF-Token': getCsrfToken(),
    },
    body: form,
  })
  return handleResponse<AudioToWorkOrderResult>(res)
}

export async function approveBrainWorkOrder(id: string, notes?: string): Promise<WorkOrder> {
  const res = await fetch(`/api/brain/work-orders/${encodeURIComponent(id)}/approve`, {
    method: 'POST',
    credentials: 'include',
    headers: mutationHeaders(),
    body: JSON.stringify({ notes }),
  })
  return handleResponse<WorkOrder>(res)
}

export async function rejectBrainWorkOrder(id: string, reason?: string): Promise<WorkOrder> {
  const res = await fetch(`/api/brain/work-orders/${encodeURIComponent(id)}/reject`, {
    method: 'POST',
    credentials: 'include',
    headers: mutationHeaders(),
    body: JSON.stringify({ reason }),
  })
  return handleResponse<WorkOrder>(res)
}

export async function completeBrainWorkOrder(
  id: string,
  data?: { notes?: string; result?: Record<string, unknown>; commit?: string; version?: string },
): Promise<WorkOrder> {
  const res = await fetch(`/api/brain/work-orders/${encodeURIComponent(id)}/complete`, {
    method: 'POST',
    credentials: 'include',
    headers: mutationHeaders(),
    body: JSON.stringify(data || {}),
  })
  return handleResponse<WorkOrder>(res)
}

export async function fetchPendingApprovals(): Promise<BrainApproval[]> {
  const res = await fetch('/api/brain/approvals/pending', { credentials: 'include' })
  const payload = await handleResponse<{ approvals?: BrainApproval[]; count?: number }>(res)
  return Array.isArray(payload.approvals) ? payload.approvals : []
}

export async function decideBrainApproval(id: string, status: 'approved' | 'rejected'): Promise<BrainApproval> {
  const res = await fetch(`/api/brain/approvals/${encodeURIComponent(id)}/decision`, {
    method: 'POST',
    credentials: 'include',
    headers: mutationHeaders(),
    body: JSON.stringify({ status }),
  })
  return handleResponse<BrainApproval>(res)
}

// --- Messages ---

export async function fetchBrainMessages(opts?: {
  limit?: number
  includeAcked?: boolean
  includeSent?: boolean
}): Promise<{ messages: BrainMessage[]; count: number }> {
  const params = new URLSearchParams()
  if (opts?.limit) params.set('limit', String(opts.limit))
  if (opts?.includeAcked) params.set('includeAcked', 'true')
  if (opts?.includeSent) params.set('includeSent', 'true')
  const qs = params.toString()
  const res = await fetch(`/api/brain/messages${qs ? '?' + qs : ''}`, { credentials: 'include' })
  return handleResponse<{ messages: BrainMessage[]; count: number }>(res)
}

export type InboxSource = 'admp' | 'fallback' | 'local-file'

/** Fetch from the ADMP-backed inbox endpoint. Falls back to brain server if ADMP not configured. */
export async function fetchInboxMessages(opts?: {
  type?: string
  includeSent?: boolean
}): Promise<{ messages: BrainMessage[]; source: InboxSource; cached: boolean }> {
  const params = new URLSearchParams()
  if (opts?.type) params.set('type', opts.type)
  if (opts?.includeSent) params.set('include_sent', 'true')
  const qs = params.toString()
  const res = await fetch(`/api/brain/inbox${qs ? '?' + qs : ''}`, { credentials: 'include' })
  return handleResponse<{ messages: BrainMessage[]; source: InboxSource; cached: boolean }>(res)
}

export async function ackBrainMessage(id: string): Promise<{ success: boolean }> {
  const res = await fetch(`/api/brain/messages/${encodeURIComponent(id)}/ack`, {
    method: 'POST',
    credentials: 'include',
    headers: mutationHeaders(),
  })
  return handleResponse<{ success: boolean }>(res)
}

export async function sendBrainMessage(data: {
  to: string
  type: string
  subject: string
  body: string
}): Promise<{ success: boolean; messageId?: string }> {
  const res = await fetch('/api/brain/messages', {
    method: 'POST',
    credentials: 'include',
    headers: mutationHeaders(),
    body: JSON.stringify(data),
  })
  return handleResponse<{ success: boolean; messageId?: string }>(res)
}

export async function fetchBrainAgents(): Promise<{ agents: BrainAgent[]; count: number }> {
  const res = await fetch('/api/brain/agents', { credentials: 'include' })
  return handleResponse<{ agents: BrainAgent[]; count: number }>(res)
}

// --- Costs ---

export async function fetchDailyCosts(): Promise<DailyCosts> {
  const res = await fetch('/api/brain/costs', { credentials: 'include' })
  return handleResponse<DailyCosts>(res)
}

// --- Brains panel (brain-mount-v1 Phase 4) ---

export async function fetchMountedBrains(): Promise<MountedBrain[]> {
  const res = await fetch('/api/brains/mounts', { credentials: 'include' })
  return handleResponse<MountedBrain[]>(res)
}

/** Throws on non-ok responses — callers must use Promise.allSettled or try/catch. */
export async function fetchBrainCards(env = 'decisive'): Promise<AgentCard[]> {
  const res = await fetch(`/api/brains/cards?env=${encodeURIComponent(env)}`, { credentials: 'include' })
  return handleResponse<AgentCard[]>(res)
}

export async function remountBrain(brainId: string): Promise<void> {
  const res = await fetch('/api/brains/mount', {
    method: 'POST',
    credentials: 'include',
    headers: mutationHeaders(),
    body: JSON.stringify({ brain_id: brainId }),
  })
  await handleResponse<unknown>(res)
}

// streamBrainChat blocked on mech-plane POST /orchestrate shape (msg-1776275208755-fpsf7z)
